import { Close } from '@mui/icons-material'
import { Box, DialogTitle, IconButton, Stack, Theme, Typography, styled } from '@mui/material'
import { Context, FC, ReactNode } from 'react'
import GenericInformationTooltip from 'src/components/common/GenericInformationTootip'
import { IMainComponentType } from 'src/enums'
import { ILocality } from 'src/generated-types'
import { ICertificate } from 'src/generated-types/Certificate'
import { IMainComponent } from 'src/generated-types/main-component'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getEquipmentTooltipData, getThemeColor } from 'src/utils'
import { formatMainComponentIdentification } from 'src/utils/function'
import { EquipmentDetailsPopover } from '../detailModal/EquipmentDetailsPopover'
import CertificateComponent from './CertificateComponent'
import EquipmentModalProgressBar from './EquipmentModalProgressBar'

interface IEquipmentHeaderSectionProps {
  pop?: () => void
  theme: Theme
  data: IMainComponent
  certificates?: ICertificate[]
  customerId?: EntityId
  locality?: ILocality
  disabledAddButton?: boolean
  context?: Context<any> | null
  topAction?: ReactNode
  isReadOnly?: boolean
}

export const EquipmentHeaderSection: FC<IEquipmentHeaderSectionProps> = ({
  pop,
  theme,
  data,
  certificates,
  customerId,
  locality,
  disabledAddButton,
  context,
  topAction,
  isReadOnly,
}) => {
  return (
    <Stack height={'130px'}>
      <HeaderContainer theme={theme}>
        <StyledDialogTitle>
          <HeaderStack>
            <HeaderTitle theme={theme}>
              <GenericInformationTooltip data={getEquipmentTooltipData(data)}>
                {formatMainComponentIdentification(data)}
              </GenericInformationTooltip>
            </HeaderTitle>
            <Flex.Row alignItems="center" gap={1}>
              {topAction}
              {pop && (
                <CloseIconButton aria-label="close" onClick={pop} theme={theme}>
                  <Close />
                </CloseIconButton>
              )}
            </Flex.Row>
          </HeaderStack>
        </StyledDialogTitle>

        <CertificateContainer>
          <Flex.Row gap={2} alignItems="center">
            <CertificateComponent
              certificates={certificates ?? []}
              mainComponent={data ?? undefined}
              customerId={customerId as EntityId}
              disabledAddButton={disabledAddButton || isReadOnly}
              hasPendingService={data.hasPendingService}
              context={context}
            />

            {certificates &&
              certificates.length > 0 &&
              (data.type.componentType === IMainComponentType.Net ||
                data.type.componentType === IMainComponentType.Ring) && (
                <Flex.Column px={2} py={1} spacing={1}>
                  <EquipmentModalProgressBar
                    mainComponent={data}
                    certificates={certificates}
                    showExtraDetails={false}
                  />
                </Flex.Column>
              )}
          </Flex.Row>
        </CertificateContainer>
      </HeaderContainer>
      <KeySpecsContainer>
        <EquipmentDetailsPopover equipment={data} locality={locality} customerId={customerId as EntityId} />
      </KeySpecsContainer>
    </Stack>
  )
}

const HeaderContainer = styled(Box)<{ theme: Theme }>(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  minHeight: '90px',
}))

const StyledDialogTitle = styled(DialogTitle)({
  padding: '12px 16px 0 16px',
})

const HeaderStack = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '-5px',
})

const HeaderTitle = styled(Typography)<{ theme: Theme }>(({ theme }) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 700,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const CloseIconButton = styled(IconButton)<{ theme: Theme }>(({ theme }) => ({
  '& svg': {
    color: getThemeColor(theme, MUIThemeColors.secondaryMain),
    fontSize: '18px',
  },
}))

const CertificateContainer = styled(Box)({
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingBottom: '4px',
})

const KeySpecsContainer = styled(Box)({
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '8px',
  paddingBottom: '8px',
})
